// src/App.js
import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "./App.css";
import dashboardImage from "./images/dashboard-preview.png"; // Ensure you have this image in your assets folder
import {
  FaUsers,
  FaBook,
  FaChartLine,
  FaCalendarAlt,
  FaLaptop,
  FaLock,
  FaCheck,
} from "react-icons/fa";

// Components
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="header">
      <div className="container">
        <div className="logo">
          <Link to="/">Tejas Technologies</Link>
        </div>

        <div
          className={`nav-toggle ${isMenuOpen ? "active" : ""}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        <nav className={`nav-menu ${isMenuOpen ? "active" : ""}`}>
          <ul>
            <li>
              <Link to="/" onClick={() => setIsMenuOpen(false)}>
                Home
              </Link>
            </li>
            <li>
              <Link to="/features" onClick={() => setIsMenuOpen(false)}>
                Features
              </Link>
            </li>
            <li>
              <Link to="/modules" onClick={() => setIsMenuOpen(false)}>
                Modules
              </Link>
            </li>
            <li>
              <Link to="/pricing" onClick={() => setIsMenuOpen(false)}>
                Pricing
              </Link>
            </li>
            <li>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                Contact
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

const Hero = () => (
  <section className="hero">
    <div className="container">
      <div className="hero-content">
        <h1>University Management Reinvented</h1>
        <p>
          Comprehensive software solution for modern educational institutions
        </p>
        <div className="cta-buttons">
          {/* <Link to="/demo" className="btn primary">Request Demo</Link> */}
          <Link to="/features" className="btn secondary">
            Explore Features
          </Link>
        </div>
      </div>
      <div className="hero-image">
        <div className="dashboard-preview">
          <img
            src={dashboardImage}
            alt="Software dashboard showing university analytics and management tools with charts, navigation menu, and summary cards. Text displayed includes University Management Reinvented and Comprehensive software solution for modern educational institutions. The environment is clean and professional, conveying a sense of innovation and reliability."
            width="100%"
            height="100%"
          />
        </div>
      </div>
    </div>
  </section>
);

const FeaturesSection = () => {
  const features = [
    {
      id: 1,
      icon: "📊",
      title: "Exam Form",
      description:
        "Manage comprehensive student exam forms and records efficiently.",
    },
    {
      id: 2,
      icon: "💳",
      title: "Payment Gateway",
      description:
        "Secure and seamless payment integration for fees and forms.",
    },
    {
      id: 3,
      icon: "📝",
      title: "Exam Result",
      description:
        "Generate and publish exam results with automated calculations.",
    },
    {
      id: 4,
      icon: "📚",
      title: "Result Ledgers",
      description:
        "Access detailed result ledgers for students and departments.",
    },
    {
      id: 5,
      icon: "🎓",
      title: "Convocation Fees",
      description:
        "Collect and manage convocation fees and student participation.",
    },
    {
      id: 6,
      icon: "🔒",
      title: "Secure Access",
      description: "Ensure secure platform access with role-based permissions.",
    },
    {
      id: 7,
      icon: "📱",
      title: "Mobile App Support",
      description: "Dedicated mobile apps for students, faculty, and admins.",
    },
  ];

  return (
    <section className="features">
      <div className="container">
        <h2>Powerful Features</h2>
        <p className="section-subtitle">
          Everything you need to manage educational institutions efficiently
        </p>

        <div className="features-grid">
          {features.map((feature) => (
            <div className="feature-card" key={feature.id}>
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Modules = () => {
  const modules = [
    {
      id: 1,
      title: "Student Information System",
      icon: <FaUsers />,
      features: [
        "Comprehensive student profiles",
        "Admissions management",
        "Registration and enrollment",
        "Academic records",
        "Attendance tracking",
      ],
      description:
        "Centralized platform for managing all student-related activities from admission to graduation.",
    },
    {
      id: 2,
      title: "Academic Management",
      icon: <FaBook />,
      features: [
        "Course catalog management",
        "Class scheduling",
        "Faculty workload management",
        "Curriculum design",
        "Academic calendar",
      ],
      description:
        "Streamline course planning, scheduling, and resource allocation across departments.",
    },
    {
      id: 3,
      title: "Examination System",
      icon: <FaLaptop />,
      features: [
        "Exam scheduling",
        "Result processing",
        "Grade management",
        "Online assessment",
        "Analytics and reports",
      ],
      description:
        "End-to-end exam management solution with automated processing and secure access.",
    },
    {
      id: 4,
      title: "Finance & Accounting",
      icon: <FaChartLine />,
      features: [
        "Fee management",
        "Financial reporting",
        "Budget planning",
        "Payroll processing",
        "Grant management",
      ],
      description:
        "Integrated financial management tailored for educational institutions.",
    },
    {
      id: 5,
      title: "Human Resources",
      icon: <FaCalendarAlt />,
      features: [
        "Staff management",
        "Recruitment",
        "Performance evaluation",
        "Leave management",
        "Professional development",
      ],
      description:
        "Comprehensive HR solution for managing faculty and administrative staff.",
    },
    {
      id: 6,
      title: "Campus Management",
      icon: <FaLock />,
      features: [
        "Facility scheduling",
        "Inventory management",
        "Library system",
        "Hostel management",
        "Security systems",
      ],
      description:
        "Integrated solution for managing campus resources and infrastructure.",
    },
  ];

  return (
    <section className="page-content modules">
      <div className="container">
        <div className="section-header">
          <h1>Comprehensive Modules</h1>
          <p>
            Our software offers specialized modules to address every aspect of
            university management
          </p>
        </div>

        <div className="modules-grid">
          {modules.map((module) => (
            <div className="module-card" key={module.id}>
              <div className="module-header">
                <div className="module-icon">{module.icon}</div>
                <h3>{module.title}</h3>
              </div>
              <p className="module-description">{module.description}</p>
              <ul className="module-features">
                {module.features.map((feature, index) => (
                  <li key={index}>
                    <FaCheck className="feature-check" /> {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Page
const Pricing = () => {
  const plans = [
    {
      id: 1,
      name: "",
      description: "",
      price: "",
      period: "",
      features: [
        
      ],
      recommended: false,
    },
    {
      id: 2,
      name: "Enterprise",
      description: "For large universities with complex needs",
      price: "Custom",
      period: "tailored solution",
      features: [
        "All Professional Features",
        "Human Resources",
        "Campus Management",
        "24/7 Dedicated Support",
        "Advanced Analytics",
        "API Integrations",
        "Unlimited Students",
      ],
      recommended: true,
    },

    {
      id: 3,
      name: "",
      description: "",
      price: "",
      period: "",
      features: [
        ,
      ],
      recommended: false,
    },
  ];

  return (
    <section className="page-content pricing">
      <div className="container">
        <div className="section-header">
          <h1>Simple, Transparent Pricing</h1>
          <p>Choose the plan that works best for your institution</p>
        </div>

        <div className="pricing-cards">
          {plans.map((plan) => (
            <div
              className={`pricing-card ${
                plan.recommended ? "recommended" : ""
              }`}
              key={plan.id}
            >
              {plan.recommended && (
                <div className="recommended-badge">Most Popular</div>
              )}
              <h3>{plan.name}</h3>
              <p className="plan-description">{plan.description}</p>

              <div className="price">
                <span>{plan.price}</span>
                <span className="period">{plan.period}</span>
              </div>

              <ul className="features">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <FaCheck className="feature-check" /> {feature}
                  </li>
                ))}
              </ul>

              <Link to="/contact" className="btn primary">
                Get Started
              </Link>
            </div>
          ))}
        </div>

        <div className="enterprise-offer">
          <h3>Custom Enterprise Solutions</h3>
          <p>
            Need something tailored to your institution's specific requirements?
            Contact us for a custom solution that fits your unique needs.
          </p>
          <Link to="/contact" className="btn outline">
            Request a Custom Quote
          </Link>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-about">
          <h3>Tejas Technologies</h3>
          <p>
            Comprehensive university management solutions for the digital age.
          </p>
        </div>

        <div className="footer-links">
          <h4>Quick Links</h4>
          <ul>
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/features">Features</Link>
            </li>
            <li>
              <Link to="/pricing">Pricing</Link>
            </li>
            <li>
              <Link to="/contact">Contact</Link>
            </li>
          </ul>
        </div>

        <div className="footer-contact">
          <h4>Contact Us</h4>
          <ul>
            <li>Email: info@euniversity-software.com</li>
            <li>Phone: +91 (555) 123-4567</li>
            <li>Address: Nashik, Maharashtra, India. 422001</li>
          </ul>
        </div>
      </div>

      <div className="footer-bottom">
        <p>
          &copy; {new Date().getFullYear()} Tejas Technologies. All rights
          reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Pages
const Home = () => (
  <>
    <Hero />
    <FeaturesSection />
    <Modules />
    <Pricing />
    
  </>
);

const featuresData = {
  "For Students": [
    "Theory Exam Time Table",
    "Theory Exam Centre List",
    "Practical Exam Batchwise Time Table",
    "Student Profile Update",
    "Online Exam Form",
    "Photocopy",
    "Results",
  ],
  "For Teachers": [
    "Practical Exam Marks Submission",
    "Paper-setting and Moderation",
    "Teacher Profile Update",
    "Syllabus",
    "Old Question Papers",
  ],
  "For Colleges": [
    "Hall Tickets",
    "IA Marks Submission",
    "Online Teachers Database",
    "Exam Manuals",
    "Ordinances",
    "Board of Exams",
  ],
};

// Features Section
const Features = () => {
  return (
    <section className="page-content">
      <div className="container">
        <h1>Advanced Features</h1>
        <div className="feature-grid">
          {Object.entries(featuresData).map(([category, items], idx) => (
            <div className="feature-card" key={idx}>
              <h2>{category}</h2>
              <ul>
                {items.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://formsubmit.co/ajax/fqthedeveloper@gmail.com",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            number: formData.number,
            message: formData.message,
            _subject: "New Contact Form Submission",
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", message: "" });
      } else {
        setSubmitStatus("error");
        console.error("Form submission error:", result);
      }
    } catch (error) {
      setSubmitStatus("error");
      console.error("Network error:", error);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <section className="page-content">
      <div className="container">
        <h1>Contact Us</h1>

        <div className="contact-container">
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <p>
              Have questions about our software? Our team is ready to help you.
            </p>
            <ul>
              <li>Email: support@euniversity-software.com</li>
              <li>Phone: +91 </li>
            </ul>
          </div>

          <div className="contact-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Phone Number</label>
                <input
                  type="number"
                  id="number"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              {submitStatus === "success" && (
                <div className="success-message">
                  Thank you for your message! We'll contact you soon.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="error-message">
                  Error sending message. Please try again later.
                </div>
              )}

              <button
                type="submit"
                className="btn primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <div className="App">
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/modules" element={<Modules />} />
            <Route path="/pricing" element={<Pricing />} />
            {/* Add other routes as needed */}
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
